import { useState, useEffect, useCallback } from 'react';
import { fetchAllWarranties, updateWarrantyStatus } from '../utils/api';

/**
 * 保修單數據處理Hook
 * 用於獲取和管理保修單數據
 * 
 * @param {string} token - 認證令牌
 * @returns {Object} 保修單數據和操作方法
 */
function useWarranty(token) {
    // 狀態管理
    const [warranties, setWarranties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [updateError, setUpdateError] = useState(null);

    // 獲取所有保修單
    const loadWarranties = useCallback(async () => {
        try {
            setLoading(true);
            const data = await fetchAllWarranties(token);
            setWarranties(data);
            setError(null);
        } catch (err) {
            console.error('獲取保修單列表失敗:', err);
            setError('獲取保修單數據時出錯');
        } finally {
            setLoading(false);
        }
    }, [token]);

    // 初始加載
    useEffect(() => {
        if (token) {
            loadWarranties();
        }
    }, [token, loadWarranties]);

    // 更新保修單狀態
    const updateStatus = useCallback(async (warrantyId, newStatus, notes = '') => {
        try {
            setUpdateLoading(true);
            // console.log(`嘗試更新保養單狀態: ID=${warrantyId}, 新狀態=${newStatus}`);

            const result = await updateWarrantyStatus(warrantyId, newStatus, notes, token);
            // console.log('保養單狀態更新結果:', result);

            // 更新本地狀態
            setWarranties(prevWarranties =>
                prevWarranties.map(warranty =>
                    warranty.id === warrantyId ? {
                        ...warranty,
                        verification_status: newStatus,
                        verification_notes: notes,
                        verification_date: new Date().toISOString(),
                        staff_name: localStorage.getItem('username') || 'Unknown'
                    } : warranty
                )
            );

            setUpdateError(null);
            return true;
        } catch (err) {
            console.error('更新保修單狀態失敗:', err);

            // 提取更詳細的錯誤信息
            let errorMessage = '更新保修單狀態時出錯';
            if (err.response && err.response.data) {
                if (err.response.data.error) {
                    errorMessage = err.response.data.error;
                }
                if (err.response.data.details) {
                    errorMessage += `: ${JSON.stringify(err.response.data.details)}`;
                }
                if (err.response.data.userErrors) {
                    errorMessage += `: ${JSON.stringify(err.response.data.userErrors)}`;
                }
            }

            setUpdateError(errorMessage);
            return false;
        } finally {
            setUpdateLoading(false);
        }
    }, [token]);

    // 根據ID獲取保修單
    const getWarrantyById = useCallback((warrantyId) => {
        return warranties.find(warranty =>
            warranty.id === warrantyId ||
            warranty.id.split('/').pop() === warrantyId
        );
    }, [warranties]);

    // 根據客戶ID獲取保修單
    const getWarrantiesByCustomerId = useCallback((customerId) => {
        return warranties.filter(warranty =>
            warranty.customerId === customerId ||
            warranty.customerId?.split('/').pop() === customerId
        );
    }, [warranties]);

    // 根據狀態篩選保修單
    const filterWarrantiesByStatus = useCallback((status) => {
        if (status === 'All') return warranties;
        return warranties.filter(warranty => warranty.status === status);
    }, [warranties]);

    // 搜索保修單
    const searchWarranties = useCallback((searchTerm) => {
        if (!searchTerm) return warranties;

        const term = searchTerm.toLowerCase();
        return warranties.filter(warranty =>
            warranty.productName?.toLowerCase().includes(term) ||
            warranty.customerName?.toLowerCase().includes(term) ||
            warranty.customerEmail?.toLowerCase().includes(term) ||
            warranty.notes?.toLowerCase().includes(term)
        );
    }, [warranties]);

    return {
        warranties,
        loading,
        error,
        updateLoading,
        updateError,
        loadWarranties,
        updateStatus,
        getWarrantyById,
        getWarrantiesByCustomerId,
        filterWarrantiesByStatus,
        searchWarranties
    };
}

export default useWarranty; 